/**
 * Modular Swiper Utility Functions
 * Reusable Swiper initialization for different slider types
 */

(function () {
    'use strict';

    // Store Swiper instances
    const swiperInstances = new Map();

    /**
     * Initialize a Swiper slider with custom configuration
     * @param {Object} config - Configuration object
     * @param {string} config.id - Swiper container ID
     * @param {Object} config.options - Swiper options
     * @param {string} config.navPrevSelector - Previous button selector
     * @param {string} config.navNextSelector - Next button selector
     * @param {Function} config.onInit - Optional callback on initialization
     */
    function initSwiper(config) {
        const {
            id,
            options = {},
            navPrevSelector,
            navNextSelector,
            onInit
        } = config;

        if (!id) {
            console.warn('Swiper init: ID is required');
            return null;
        }

        const swiperElement = document.getElementById(id);
        if (!swiperElement) {
            console.warn(`Swiper init: Element with ID "${id}" not found`);
            return null;
        }

        // Destroy existing instance if it exists
        if (swiperInstances.has(id)) {
            try {
                const existingInstance = swiperInstances.get(id);
                existingInstance.destroy(true, true);
            } catch (e) {
                console.warn(`Error destroying Swiper instance "${id}":`, e);
            }
            swiperInstances.delete(id);
        }

        if (typeof Swiper === 'undefined') {
            console.warn('Swiper.js is not loaded');
            return null;
        }

        // Default options
        const defaultOptions = {
            loop: false,
            watchOverflow: true,
            autoHeight: false,
            observer: true,
            observeParents: true,
            on: {
                init: function () {
                    this.update();
                    if (onInit) onInit(this);
                },
                imagesReady: function () {
                    this.update();
                }
            }
        };

        // Add navigation if selectors provided
        if (navPrevSelector && navNextSelector) {
            defaultOptions.navigation = {
                nextEl: navNextSelector,
                prevEl: navPrevSelector,
            };
        }

        // Merge user options with defaults
        const swiperOptions = {
            ...defaultOptions,
            ...options,
            on: {
                ...defaultOptions.on,
                ...(options.on || {})
            }
        };

        // Initialize Swiper
        setTimeout(function () {
            const swiperInstance = new Swiper(`#${id}`, swiperOptions);
            swiperInstances.set(id, swiperInstance);

            // Handle image loading for height consistency
            const images = swiperElement.querySelectorAll('img, video');
            let loadedCount = 0;
            const totalMedia = images.length;

            if (totalMedia > 0) {
                images.forEach(function (media) {
                    const isLoaded = media.tagName === 'VIDEO'
                        ? media.readyState >= 2
                        : media.complete;

                    if (isLoaded) {
                        loadedCount++;
                    } else {
                        const loadEvent = media.tagName === 'VIDEO' ? 'loadeddata' : 'load';
                        media.addEventListener(loadEvent, function () {
                            loadedCount++;
                            if (loadedCount === totalMedia && swiperInstance) {
                                swiperInstance.update();
                            }
                        });
                    }
                });

                if (loadedCount === totalMedia && swiperInstance) {
                    swiperInstance.update();
                }
            }
        }, 100);

        return swiperInstances.get(id);
    }

    /**
     * Initialize New Arrivals Product Slider
     */
    function initNewArrivalsSlider() {
        return initSwiper({
            id: 'newArrivalsSwiper',
            navPrevSelector: '.new-arrivals-nav-prev',
            navNextSelector: '.new-arrivals-nav-next',
            options: {
                slidesPerView: 1.6,
                slidesPerGroup: 1,
                spaceBetween: 6,
                breakpoints: {
                    640: {
                        slidesPerView: 2,
                        slidesPerGroup: 1,
                        spaceBetween: 6,
                    },
                    768: {
                        slidesPerView: 2,
                        slidesPerGroup: 1,
                        spaceBetween: 8,
                    },
                    1024: {
                        slidesPerView: 4,
                        slidesPerGroup: 1,
                        spaceBetween: 5,
                    },
                },
            }
        });
    }

    /**
     * Initialize Video Slider
     */
    function initVideoSlider() {
        return initSwiper({
            id: 'videoSlider',
            navPrevSelector: '.video-slider-nav-prev',
            navNextSelector: '.video-slider-nav-next',
            options: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 5,
                breakpoints: {
                    640: {
                        slidesPerView: 1.5,
                        slidesPerGroup: 1,
                        spaceBetween: 20,
                    },
                    768: {
                        slidesPerView: 2,
                        slidesPerGroup: 1,
                        spaceBetween: 24,
                    },
                    1024: {
                        slidesPerView: 4,
                        slidesPerGroup: 1,
                        spaceBetween: 10,
                    },
                },
            }
        });
    }

    /**
     * Initialize Recently Viewed Products Slider
     */
    function initRecentlyViewedSlider() {
        return initSwiper({
            id: 'recentlyViewedSwiper',
            navPrevSelector: '.recently-viewed-nav-prev',
            navNextSelector: '.recently-viewed-nav-next',
            options: {
                slidesPerView: 2,
                slidesPerGroup: 1,
                spaceBetween: 0,
                breakpoints: {
                    1024: {
                        slidesPerView: 4,
                        slidesPerGroup: 1,
                        spaceBetween: 5,
                    },
                },
            }
        });
    }

    /**
     * Reinitialize all sliders (useful for theme editor)
     */
    function reinitAllSliders() {
        initNewArrivalsSlider();
        initVideoSlider();
        initRecentlyViewedSlider();
    }

    // Export functions to window
    window.SwiperUtils = {
        init: initSwiper,
        initNewArrivalsSlider: initNewArrivalsSlider,
        initVideoSlider: initVideoSlider,
        initRecentlyViewedSlider: initRecentlyViewedSlider,
        reinitAll: reinitAllSliders
    };

    // Auto-initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            initNewArrivalsSlider();
            initVideoSlider();
        });
    } else {
        initNewArrivalsSlider();
        initVideoSlider();
    }

    // Re-initialize for Shopify theme editor
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', function () {
            setTimeout(reinitAllSliders, 200);
        });
        document.addEventListener('shopify:section:reorder', function () {
            setTimeout(reinitAllSliders, 200);
        });
        document.addEventListener('shopify:block:select', function () {
            setTimeout(reinitAllSliders, 200);
        });
    }
})();
